import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Gift, Copy, CheckCircle, Share2, ArrowRight } from 'lucide-react';
import { Button } from '../buttons/Button';

export const StudentVoucherAd: React.FC = () => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);

    const handleAction = () => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }

        if (user?.referralCode) {
            // Create the link
            const link = `${window.location.origin}/register?ref=${user.referralCode}`;
            navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } else {
            // Fallback if no code (should exist for students)
            navigate('/student/dashboard');
        }
    };

    return (
        <section className="bg-gradient-to-r from-[#2f5c96] to-[#1e3a5f] py-12 relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-white/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-60 h-60 bg-[#c19cca]/20 rounded-full blur-3xl"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="flex flex-col md:flex-row items-center justify-between gap-8">

                    {/* Text Content */}
                    <div className="flex-1 text-center md:text-left text-white">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-blue-100 mb-4 border border-white/10">
                            <Gift className="w-4 h-4" />
                            <span>Student Exclusive</span>
                        </div>
                        <h2 className="text-3xl lg:text-4xl font-extrabold mb-4 leading-tight">
                            Get Your R300 <span className="text-[#c19cca]">Student Voucher</span>
                        </h2>
                        <p className="text-blue-100 text-lg mb-8 max-w-xl mx-auto md:mx-0">
                            Invite friends to Indigo Beans Rooms and earn rewards! Get cash back for every friend who books a room.
                        </p>

                        {!isAuthenticated ? (
                            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                                <Button
                                    onClick={() => navigate('/login')}
                                    className="bg-white text-[#2f5c96] hover:bg-gray-100 border-none text-lg px-8 shadow-lg shadow-black/10"
                                >
                                    Log in to Claim
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => navigate('/register')}
                                    className="text-white border-white hover:bg-white/10 hover:text-white"
                                >
                                    New here? Register
                                </Button>
                            </div>
                        ) : (
                            <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl max-w-md mx-auto md:mx-0">
                                <p className="text-sm text-blue-100 mb-2 uppercase tracking-wider font-semibold">Your Unique Referral Link</p>
                                <div className="flex gap-2">
                                    <div className="flex-1 bg-black/20 rounded-lg flex items-center px-4 py-3 text-white font-mono text-sm truncate border border-white/10">
                                        {user?.referralCode
                                            ? `${window.location.origin}/register?ref=${user.referralCode}`
                                            : 'Loading code...'}
                                    </div>
                                    <Button
                                        onClick={handleAction}
                                        className={`min-w-[100px] border-none ${copied ? 'bg-green-500 hover:bg-green-600' : 'bg-[#c19cca] hover:bg-[#b08bb9]'} text-white shadow-lg`}
                                    >
                                        {copied ? (
                                            <><CheckCircle className="w-4 h-4 mr-2" /> Copied</>
                                        ) : (
                                            <><Copy className="w-4 h-4 mr-2" /> Copy</>
                                        )}
                                    </Button>
                                </div>
                                <p className="text-xs text-blue-200 mt-3 flex items-center gap-1">
                                    <Share2 className="w-3 h-3" /> Share this link to start earning rewards immediately.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Visual/Image */}
                    <div className="flex-1 w-full max-w-md lg:max-w-lg hidden md:block">
                        <div className="relative">
                            {/* Floating Cards Effect */}
                            <div className="absolute top-0 right-10 bg-white p-4 rounded-xl shadow-2xl transform rotate-6 z-20 animate-float-slow">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                        <DollarSign className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 font-bold">You Earned</p>
                                        <p className="text-lg font-bold text-gray-900">R 300.00</p>
                                    </div>
                                </div>
                            </div>

                            <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-2 z-10">
                                <img
                                    src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80"
                                    alt="Happy Students"
                                    className="rounded-2xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

// Simple floating animation helper
const DollarSign = (props: any) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
);
